/**
 * Autism signposting options for the triage decision wizard (RMS-309 model).
 *
 * The list is BTH's NDP information pack for parents ("Fylde Coast School-Age
 * Neurodevelopmental Pathway: What help is available, based on need", August
 * 2026), grouped under the pack's own headings with one tick per entry
 * (TDI-454; Tom King, 23 October 2026: the pack replaces Alicia Russell's 12
 * key options). The clinician ticks the entries to signpost; each ticked
 * entry puts the pack's own paragraph, links and contact details into the
 * outcome letter's "Information and support" section, assembled server-side
 * (TDI-455). "Other, tell us more" carries a clinician-written title and text.
 *
 * The ids, labels and groups mirror the backend's authoritative list in
 * python-functions/constants/rms_autism_signposting_pack.py, which also holds
 * the paragraphs. The server rejects unknown ids and always writes its own
 * label, and parity tests on both sides pin the two lists to each other: keep
 * one entry per line.
 */
export const AUTISM_SIGNPOSTING_GROUPS = [
  { groupId: 'financial_help', heading: 'Financial help' },
  { groupId: 'local_offer', heading: 'Local Offer' },
  { groupId: 'local_support_services', heading: 'Local support services' },
  { groupId: 'societies_groups_organisations', heading: 'Societies, groups and organisations that offer help and support' },
  { groupId: 'sensory_needs', heading: 'Sensory needs' },
  { groupId: 'sleep', heading: 'Sleep' },
  { groupId: 'toileting', heading: 'Toileting' },
  { groupId: 'play_sports_youth_clubs', heading: 'Play centres, sports clubs, groups and youth clubs' },
  { groupId: 'emotional_mental_health', heading: 'Emotional and mental health support' },
  { groupId: 'books_about_autism', heading: 'Books about autism' },
  { groupId: 'videos', heading: 'Videos' },
  { groupId: 'other_useful_websites', heading: 'Other useful websites' },
];

export const AUTISM_SIGNPOSTING_OPTIONS = [
  { optionId: 'disability_living_allowance', label: 'Disability Living Allowance', group: 'financial_help' },
  { optionId: 'carers_allowance', label: 'Carers Allowance', group: 'financial_help' },
  { optionId: 'blackpool_local_offer', label: 'Blackpool Local Offer', group: 'local_offer' },
  { optionId: 'lancashire_local_offer', label: 'Lancashire Local Offer', group: 'local_offer' },
  { optionId: 'children_and_family_wellbeing_service', label: 'Children and Family Wellbeing Service (Fylde and Wyre)', group: 'local_support_services' },
  { optionId: 'school_nursing_team', label: 'School nurses', group: 'local_support_services' },
  { optionId: 'neu_way_support', label: 'Neu Way Support', group: 'societies_groups_organisations' },
  { optionId: 'national_autistic_society', label: 'The National Autistic Society', group: 'societies_groups_organisations' },
  { optionId: 'aiming_higher', label: 'Aiming Higher', group: 'societies_groups_organisations' },
  { optionId: 'ambitious_about_autism', label: 'Ambitious about Autism', group: 'societies_groups_organisations' },
  { optionId: 'autistica', label: 'Autistica', group: 'societies_groups_organisations' },
  { optionId: 'autistic_girls_network', label: 'The Autistic Girls Network', group: 'societies_groups_organisations' },
  { optionId: 'neuroinclusive_education_network', label: 'Neuroinclusive Education Network', group: 'societies_groups_organisations' },
  { optionId: 'blackpool_carers_centre', label: 'Blackpool Carers Centre', group: 'societies_groups_organisations' },
  { optionId: 'parent_carer_forum', label: 'Parent Carer Forum', group: 'societies_groups_organisations' },
  { optionId: 'contact', label: 'Contact', group: 'societies_groups_organisations' },
  { optionId: 'autism_unlocked', label: 'Autism Unlocked', group: 'societies_groups_organisations' },
  { optionId: 'autism_central', label: 'Autism Central', group: 'societies_groups_organisations' },
  { optionId: 'sendias', label: 'SENDIASS (Special Educational Needs, and Disability Information, Advice and Support Service)', group: 'societies_groups_organisations' },
  { optionId: 'nhs_sensory_differences', label: 'Sensory needs', group: 'sensory_needs' },
  { optionId: 'sleep_charity', label: 'Sleep', group: 'sleep' },
  { optionId: 'eric', label: 'Toileting', group: 'toileting' },
  { optionId: 'a_team', label: 'The A Team', group: 'play_sports_youth_clubs' },
  { optionId: 'blackpool_tiggers', label: 'Blackpool Tiggers', group: 'play_sports_youth_clubs' },
  { optionId: 'ojs_saturday_club', label: 'OJ’s Saturday Club', group: 'play_sports_youth_clubs' },
  { optionId: 'space_centre', label: 'Space Centre', group: 'play_sports_youth_clubs' },
  { optionId: 'tram_shed_theatre_company', label: 'Tram Shed Theatre Company', group: 'play_sports_youth_clubs' },
  { optionId: 'sams_place', label: 'Sam’s Place', group: 'play_sports_youth_clubs' },
  { optionId: 'neuro_ninjas', label: 'Neuro Ninjas', group: 'play_sports_youth_clubs' },
  { optionId: 'ace', label: 'Achieve Change and Engagement ACE', group: 'emotional_mental_health' },
  { optionId: 'options4cyp', label: 'Options4CYP', group: 'emotional_mental_health' },
  { optionId: 'fylde_and_wyre_camhs', label: 'Fylde and Wyre CAMHS', group: 'emotional_mental_health' },
  { optionId: 'primary_mental_health_workers', label: 'Primary Mental Health Workers', group: 'emotional_mental_health' },
  { optionId: 'healthy_young_minds', label: 'Healthy Minds', group: 'emotional_mental_health' },
  { optionId: 'kooth', label: 'Kooth', group: 'emotional_mental_health' },
  { optionId: 'key_charity', label: 'Key Charity', group: 'emotional_mental_health' },
  { optionId: 'blackpool_childrens_learning_disability_service', label: 'Blackpool Children’s Learning Disability Service', group: 'emotional_mental_health' },
  { optionId: 'community_learning_disability_service_lancashire', label: 'Community Learning Disability Service (Lancashire)', group: 'emotional_mental_health' },
  { optionId: 'books_about_autism', label: 'Books about autism', group: 'books_about_autism' },
  { optionId: 'amazing_things_happen', label: 'Amazing Things Happen by Alexander Amelines', group: 'videos' },
  { optionId: 'other_useful_websites', label: 'Other useful websites', group: 'other_useful_websites' },
];

export const AUTISM_SIGNPOSTING_PROMPT =
  'Which services should the family be signposted to?';

export const AUTISM_SIGNPOSTING_HINT =
  'Optional - applies whether or not the referral was accepted. Each ticked '
  + 'entry appears in the outcome letter with the information pack’s own '
  + 'wording, links and contact details, in its own section after the sign-off.';

export const SIGNPOSTING_OTHER_LABEL = 'Other, tell us more';
